// src/ui/notify.jsx
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { createPortal } from "react-dom";
import "../styles/notify.css";

/** ------- Portal root (singleton) ------- */
function ensureRoot() {
    let el = document.getElementById("app-notify-root");
    if (!el) {
        el = document.createElement("div");
        el.id = "app-notify-root";
        document.body.appendChild(el);
    }
    return el;
}

const ICONS = {
    success: "✔️",
    info: "ℹ️",
    warning: "⚠️",
    error: "❌",
};

function Card({ kind = "info", title, message, onClose, timeout = 2500 }) {
    useEffect(() => {
        if (!timeout) return;
        const id = setTimeout(onClose, timeout);
        return () => clearTimeout(id);
    }, [onClose, timeout]);

    useEffect(() => {
        const onEsc = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [onClose]);

    return (
        <div className={`notify-card notify-${kind}`} role="status" aria-live="polite">
            <div className="notify-icon">{ICONS[kind] ?? ICONS.info}</div>
            <div className="notify-body">
                {title && <div className="notify-title">{title}</div>}
                {message && <div className="notify-message">{message}</div>}
            </div>
            <button className="notify-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
    );
}

function Container({ queue, setQueue }) {
    return createPortal(
        <div className="notify-wrap">
            {queue.map((n) => (
                <Card
                    key={n.id}
                    kind={n.kind}
                    title={n.title}
                    message={n.message}
                    timeout={n.timeout}
                    onClose={() => setQueue((q) => q.filter((x) => x.id !== n.id))}
                />
            ))}
        </div>,
        ensureRoot()
    );
}

let push; // setter exposé aux helpers

function Mount() {
    const [queue, setQueue] = useState([]);
    useEffect(() => {
        push = (item) =>
            setQueue((q) => [
                ...q,
                { id: Math.random().toString(36).slice(2), timeout: 2500, ...item },
            ]);
    }, []);
    return <Container queue={queue} setQueue={setQueue} />;
}

/** ------- Auto-mount (React 18) ------- */
(function autoMount() {
    const el = ensureRoot();
    // cache la root pour ne pas en créer plusieurs
    if (!el._root) {
        el._root = createRoot(el);
        el._root.render(<Mount />);
    }
})();

/** ------- API ------- */
export const notify = {
    show: (opts) => (push ? push({ kind: "info", ...opts }) : console.warn("notify not mounted yet")),
    info: (opts) => (push ? push({ kind: "info", ...opts }) : console.warn("notify not mounted yet")),
    success: (opts) => (push ? push({ kind: "success", ...opts }) : console.warn("notify not mounted yet")),
    warning: (opts) => (push ? push({ kind: "warning", ...opts }) : console.warn("notify not mounted yet")),
    error: (opts) => (push ? push({ kind: "error", ...opts }) : console.warn("notify not mounted yet")),
};
